/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $findMatchingParent,
  $wrapNodeInElement,
  mergeRegister,
} from '@lexical/utils';
import { $isListItemNode } from '@lexical/list';
import {
  $createLineBreakNode,
  $createNodeSelection,
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $normalizeSelection__EXPERIMENTAL,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  LexicalCommand,
  LexicalEditor,
  OUTDENT_CONTENT_COMMAND,
  SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
} from 'lexical';
import * as React from 'react';
import { type JSX, useEffect, useRef, useState } from 'react';
import { CAN_USE_DOM } from '@/components/LexicalEditor/shared/canUseDOM';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';

import {
  $createImageNode,
  $isImageNode,
  ImageNode,
  ImagePayload,
} from '../../nodes/ImageNode';
import {
  $getPastedImagesOnly,
  $getSelectedListItem,
  $insertImageInListItem,
  $moveImageIntoListAbove,
  $moveImageOutOfList,
} from '../../utils/listItemImage';
import Button from '../../ui/Button';
import { Button as MuiButton, CircularProgress, Stack } from '@mui/material';
import { DialogActions, DialogButtonsList } from '../../ui/Dialog';
import FileInput from '../../ui/FileInput';
import TextInput from '../../ui/TextInput';
import { uploadImage } from '@/components/file-thumbnail/utils';
import { sanitizeUrl } from '@/components/LexicalEditor/utils/url';
import { useSnackbar } from '@/hooks/global-tools';
import { useTranslation } from 'react-i18next';

export type InsertImagePayload = Readonly<ImagePayload>;

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> =
  createCommand('INSERT_IMAGE_COMMAND');

export function InsertImageUriDialogBody({
  onClick,
}: {
  onClick: (payload: InsertImagePayload, linkUrl: string) => void;
}) {
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const { t } = useTranslation();

  const isDisabled = src === '';

  return (
    <>
      <TextInput
        label={t('이미지 URL')}
        placeholder="i.e. https://source.unsplash.com/random"
        onChange={setSrc}
        value={src}
        data-test-id="image-modal-url-input"
      />
      <TextInput
        label={t('대체 텍스트')}
        placeholder={t('이미지 설명')}
        onChange={setAltText}
        value={altText}
        data-test-id="image-modal-alt-text-input"
      />
      <TextInput
        label={t('링크 URL')}
        placeholder={t('링크 URL')}
        onChange={setLinkUrl}
        value={linkUrl}
        data-test-id="image-modal-link-url-input"
      />
      <DialogActions>
        <Button
          data-test-id="image-modal-confirm-btn"
          disabled={isDisabled}
          onClick={() => onClick({ altText, src }, linkUrl)}
        >
          {t('확인')}
        </Button>
      </DialogActions>
    </>
  );
}

export function InsertImageUploadedDialogBody({
  onClick,
}: {
  onClick: (payload: InsertImagePayload, linkUrl: string) => void;
}) {
  const [src, setSrc] = useState('');
  const [altText, setAltText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [loading, setIsLoading] = useState(false);
  const toast = useSnackbar();
  const { t } = useTranslation();

  const isDisabled = src === '';

  const loadImage = (files: FileList | null) => {
    setIsLoading(true);
    if (files !== null) {
      // 이미지 크기 체크
      const img = new Image();
      const objectUrl = URL.createObjectURL(files[0]);

      img.onload = () => {
        URL.revokeObjectURL(objectUrl);

        if (img.width > 3000 || img.height > 6000) {
          toast.show({
            severity: 'error',
            message: t('이미지는 최대 3000px*6000px까지 첨부 가능합니다.'),
          });
          setIsLoading(false);
          return;
        }

        loadImageUpload(files[0]);

        if (files[0].size >= 100000000) {
          toast.show({
            severity: 'info',
            message: t(
              '용량이 큰 이미지는 불러오는 데 시간이 걸릴 수 있어요. 잠시만 기다려 주세요.',
            ),
            duration: 5000,
          });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        setIsLoading(false);
      };

      img.src = objectUrl;
    } else {
      const reader = new FileReader();
      reader.onload = function () {
        console.log('onload', reader.result);
        if (typeof reader.result === 'string') {
          setSrc(reader.result);
        }
        setIsLoading(false);
        return '';
      };
    }
  };

  const loadImageUpload = async (file: File): Promise<void> => {
    const uploadedFile = await uploadImage(file);
    setSrc(uploadedFile.url);
    setIsLoading(false);
  };

  return (
    <>
      <FileInput
        label={t('이미지 업로드')}
        onChange={loadImage}
        accept="image/*"
        data-test-id="image-modal-file-upload"
      />
      <TextInput
        label={t('대체 텍스트')}
        placeholder={t('이미지 설명')}
        onChange={setAltText}
        value={altText}
        data-test-id="image-modal-alt-text-input"
      />
      <TextInput
        label={t('링크 URL')}
        placeholder={t('링크 URL')}
        onChange={setLinkUrl}
        value={linkUrl}
        data-test-id="image-modal-link-url-input"
      />
      <DialogActions>
        <MuiButton
          disableRipple
          variant="contained"
          color={'inherit'}
          data-test-id="image-modal-file-upload-btn"
          sx={{ width: 140 }}
          loadingIndicator={
            <Stack spacing={0.5} direction={'row'} alignItems={'center'}>
              <CircularProgress color={'inherit'} size={16} />
            </Stack>
          }
          loading={loading}
          disabled={isDisabled}
          onClick={() => onClick({ altText, src }, linkUrl)}
        >
          {t('확인')}
        </MuiButton>
      </DialogActions>
    </>
  );
}

export function InsertImageDialog({
  activeEditor,
  onClose,
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
}): JSX.Element {
  const [mode, setMode] = useState<null | 'url' | 'file'>(null);
  const hasModifier = useRef(false);
  const { t } = useTranslation();

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  const onClick = (payload: InsertImagePayload, linkUrl: string) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    if (linkUrl) {
      activeEditor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(linkUrl));
    }
    onClose();
  };

  return (
    <>
      {!mode && (
        <>
          <div
            style={{
              padding: '0 16px 16px',
              color: 'var(--dk-text-strong, #666)',
              fontSize: '14px',
            }}
          >
            {t('이미지는 최대 3000px*6000px까지 첨부 가능합니다.')}
          </div>
          <DialogButtonsList>
            <Button
              data-test-id="image-modal-option-url"
              onClick={() => setMode('url')}
            >
              URL
            </Button>
            <Button
              data-test-id="image-modal-option-file"
              onClick={() => setMode('file')}
            >
              {t('파일')}
            </Button>
          </DialogButtonsList>
        </>
      )}
      {mode === 'url' && <InsertImageUriDialogBody onClick={onClick} />}
      {mode === 'file' && <InsertImageUploadedDialogBody onClick={onClick} />}
    </>
  );
}

export default function ImagesPlugin({
  captionsEnabled,
}: {
  captionsEnabled?: boolean;
}): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error('ImagesPlugin: ImageNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<InsertImagePayload>(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const listItem = payload.inline ? null : $getSelectedListItem();
          if (listItem) {
            const imageNode = $createImageNode({ ...payload, inline: true });
            $insertImageInListItem(listItem, imageNode);
            imageNode.selectNext();
            return true;
          }
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
      // 웹·문서에서 복사한 이미지(HTML 붙여넣기)도 목록 안에선 항목 안으로
      editor.registerCommand(
        SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
        ({ nodes }) => {
          const listItem = $getSelectedListItem();
          const images = listItem ? $getPastedImagesOnly(nodes) : null;
          if (!listItem || !images) return false;

          images.forEach((image, i) => {
            image.setInline(true);
            if (i === 0) {
              $insertImageInListItem(listItem, image);
            } else {
              const br = $createLineBreakNode();
              images[i - 1].insertAfter(br);
              br.insertAfter(image);
            }
          });
          images[images.length - 1].selectNext();
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return onDragover(event);
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH,
      ),
      // 이미지를 선택하고 Tab/Shift+Tab: 목록 안팎으로 옮기기, 목록 안이면 그 항목 들여쓰기·내어쓰기
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isNodeSelection(selection)) return false;
          const [image] = selection.getNodes();
          if (selection.getNodes().length !== 1 || !$isImageNode(image)) {
            return false;
          }

          const listItem = image.getParent();
          if ($isListItemNode(listItem)) {
            const isTopLevel = !$isListItemNode(
              listItem.getParent()?.getParent(),
            );
            if (event.shiftKey && isTopLevel) {
              $moveImageOutOfList(image);
            } else {
              listItem.select(0, 0);
              editor.dispatchCommand(
                event.shiftKey
                  ? OUTDENT_CONTENT_COMMAND
                  : INDENT_CONTENT_COMMAND,
                undefined,
              );
            }
          } else if (event.shiftKey || !$moveImageIntoListAbove(image)) {
            return false;
          }

          event.preventDefault();
          const imageSelection = $createNodeSelection();
          imageSelection.add(image.getKey());
          $setSelection(imageSelection);
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      // block 이미지(데코레이터) 정렬: FORMAT_ELEMENT 는 Element 만 정렬해 데코레이터엔 안 먹으므로
      // 노드 자신에 format 을 걸고 커맨드 소비. inline 이미지는 부모 문단 text-align(기본 동작)으로 정렬되므로 위임(false).
      editor.registerCommand(
        FORMAT_ELEMENT_COMMAND,
        (format) => {
          const selection = $getSelection();
          if (!$isNodeSelection(selection)) return false;
          const blockImages = selection
            .getNodes()
            .filter($isImageNode)
            .filter((node) => !node.getInline());
          if (blockImages.length === 0) return false;
          blockImages.forEach((node) => node.setFormat(format));
          return true;
        },
        COMMAND_PRIORITY_HIGH,
      ),
    );
  }, [captionsEnabled, editor]);

  return null;
}

function onDragStart(event: DragEvent): boolean {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  const target = event.target;
  const TRANSPARENT_IMAGE =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
  const img = document.createElement('img');
  img.src = TRANSPARENT_IMAGE;

  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
        href:
          target &&
          target instanceof HTMLElement &&
          target.parentElement instanceof HTMLElement &&
          target.parentElement.parentElement instanceof HTMLAnchorElement
            ? target.parentElement.parentElement.href
            : '',
      },
      type: 'image',
    }),
  );

  return true;
}

function onDragover(event: DragEvent): boolean {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
    if (data.href) {
      const index = node.getIndexWithinParent();
      const selection2 = node.getParent()?.select(index, index);
      if (selection2) {
        $setSelection($normalizeSelection__EXPERIMENTAL(selection2));
      }
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, sanitizeUrl(data.href));
      const linkParent = $findMatchingParent(node, $isLinkNode);
      if (linkParent && $isLinkNode(linkParent)) {
        linkParent.select();
      }
    }
  }
  return true;
}

function getImageNodeInSelection(): ImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertImagePayload {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image') {
    return null;
  }

  return data;
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-image') &&
    ((target.parentElement &&
      target.parentElement.closest('div.ContentEditable__root')) ||
      (target.parentElement?.parentElement &&
        target.parentElement?.parentElement.closest(
          'div.ContentEditable__root',
        )))
  );
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const target = event.target as null | Element | Document;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
      ? (target as Document).defaultView
      : (target as Element).ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}
